/**
 * React Hook for Quotation Service Integration
 * 
 * Manages quotation calculation state, progress tracking, and result storage.
 * Integrates with the quote wizard workflow.
 */

import { useState, useCallback } from 'react';
import { quotationService, QuotationRequest, QuotationResult, QuotationProgress } from '../services/quotationService';
import { validateQuotationSupport } from '../config/products/product';

export interface UseQuotationState {
  isCalculating: boolean;
  progress: QuotationProgress | null;
  error: string | null;
  canRecalculate: boolean;
}

export interface UseQuotationActions {
  startCalculation: (request: QuotationRequest, onResults?: (result: QuotationResult) => void) => Promise<void>;
  recalculate: (onResults?: (result: QuotationResult) => void) => Promise<void>;
  clearResults: () => void;
  cancelCalculation: () => void;
  validateSupport: (perils: string[]) => ReturnType<typeof validateQuotationSupport>;
}

export function useQuotation(): UseQuotationState & UseQuotationActions {
  const [isCalculating, setIsCalculating] = useState(false);
  const [progress, setProgress] = useState<QuotationProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRequest, setLastRequest] = useState<QuotationRequest | null>(null);

  const startCalculation = useCallback(async (request: QuotationRequest, onResults?: (result: QuotationResult) => void) => {
    try {
      setIsCalculating(true);
      setError(null);
      setProgress(null);
      setLastRequest(request);

      const quotationResult = await quotationService.getQuotation(request, (progressUpdate) => {
        console.log('📊 QUOTATION PROGRESS UPDATE:', progressUpdate);
        setProgress(progressUpdate);
        
        // If progress indicates error, also set error state
        if (progressUpdate.stage === 'error') {
          console.error('🔴 QUOTATION ERROR FROM PROGRESS:', progressUpdate.message);
          setError(progressUpdate.message);
        }
      });

      // Don't store results in hook state - pass to callback for Zustand storage
      if (onResults) {
        onResults(quotationResult);
      }
      
      setProgress({
        stage: 'completed',
        message: 'Quotation calculation completed successfully',
        progress: 100
      });

    } catch (err) {
      console.error('🔴 QUOTATION HOOK ERROR:', err);
      console.error('🔴 QUOTATION HOOK ERROR STACK:', err instanceof Error ? err.stack : 'No stack trace');
      console.error('🔴 QUOTATION REQUEST THAT FAILED:', request);
      
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('🔴 SETTING ERROR STATE:', errorMessage);
      setError(errorMessage);
      setProgress({
        stage: 'error',
        message: `Calculation failed: ${errorMessage}`,
        progress: 0
      });
      console.error('🔴 ERROR STATE SET - isCalculating will be set to false in finally block');
    } finally {
      setIsCalculating(false);
      console.log('📊 FINAL STATE:', { isCalculating: false, hasError: Boolean(error) });
    }
  }, [error]);

  const recalculate = useCallback(async (onResults?: (result: QuotationResult) => void) => {
    if (!lastRequest) {
      throw new Error('No previous request to recalculate');
    }
    await startCalculation(lastRequest, onResults);
  }, [lastRequest, startCalculation]);

  const clearResults = useCallback(() => {
    setProgress(null);
    setError(null);
    setIsCalculating(false);
    setLastRequest(null);
  }, []);

  const cancelCalculation = useCallback(() => {
    console.log('🛑 Cancelling quotation calculation');
    setIsCalculating(false);
    setProgress(null);
    setError('Calculation cancelled by user');
  }, []);

  const validateSupport = useCallback((perils: string[]) => {
    return validateQuotationSupport(perils);
  }, []);

  return {
    // State
    isCalculating,
    progress,
    error,
    canRecalculate: Boolean(lastRequest && !isCalculating),

    // Actions
    startCalculation,
    recalculate,
    clearResults,
    cancelCalculation,
    validateSupport
  };
}
